import type {ReactElement, UIEvent} from 'react';
import {useEffect, useState} from 'react';
import {BrandsLoadingState} from '@Components/mystuff-brands/brand-kits.types';
import {useFilterUsableBrandKits} from '@Hooks/brands/useFilterUsableBrandKits';
import {useBrandsLoadingState} from '@Hooks/brands/useBrandsLoadingState';
import {ClickableDiv} from '@Components/clickable-div';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {SkeletonBrick} from '@Components/skeleton-brick';
import {BrandKitSwatchCluster} from '@Components/brand-kit-swatch-cluster/brand-kit-swatch-cluster';
import {getBrandKitDisplayRowsPinnedFirst, getBrandKitLogoURL, getOrderedBrandColorHexes} from '@Libraries/brand-kit-display.library';
import {BRAND_KIT_SWATCH_COLORS_LIMIT} from '@Components/brand-kit-swatch-cluster/brand-kit-swatch-cluster.types';
import {onAddBrandAssetClickedHandler} from '@Components/poster-editor/library/poster-editor-library';
import {armBrandKitsRefetchOnTabReturn} from '@Libraries/brand-kit-refetch.library';
import {addFonts} from '@Libraries/font-library';
import {getFontFamilyStyleString} from '@Utils/font.util';
import {GA4EventName, trackGA4CustomEvent} from '@Libraries/ga-events';
import type {BrandKitPopupProps} from './brand-kit-popup.types';
import {BRAND_KIT_ROWS_BEFORE_SCROLL, BRAND_KIT_SKELETON_ROW_COUNT} from './brand-kit-popup.types';
import styles from './brand-kit-popup.module.scss';

export function BrandKitPopup({pinnedBrandKitId, onBrandKitSelect, isFullWidth = false}: BrandKitPopupProps): ReactElement {
  const [areFontsLoaded, setAreFontsLoaded] = useState(false);
  const [isListScrolled, setIsListScrolled] = useState(false);
  const usableBrandKits = useFilterUsableBrandKits();
  const brandKitRows = getBrandKitDisplayRowsPinnedFirst(usableBrandKits, pinnedBrandKitId);
  const brandsLoadingState: BrandsLoadingState = useBrandsLoadingState();
  const isLoading = brandsLoadingState !== BrandsLoadingState.LOADED;
  const doesListScroll = !isLoading && brandKitRows.length > BRAND_KIT_ROWS_BEFORE_SCROLL;

  const fontFamiliesKey = brandKitRows
    .map((brandKitRow) => {
      return brandKitRow.displayFontFamily ?? '';
    })
    .filter(Boolean)
    .join(',');

  useEffect(() => {
    let isCurrent = true;

    const markFontsLoaded = (): void => {
      if (isCurrent) {
        setAreFontsLoaded(true);
      }
    };

    if (fontFamiliesKey === '') {
      markFontsLoaded();
    } else {
      addFonts(fontFamiliesKey.split(','), markFontsLoaded, markFontsLoaded);
    }

    return (): void => {
      isCurrent = false;
    };
  }, [fontFamiliesKey]);

  const getRowClasses = (isSelected: boolean): string => {
    return `${styles.row} ${isSelected ? styles.selectedRow : ''}`;
  };

  const getFontFamilyStyle = (displayFontFamily: string | undefined): string | undefined => {
    if (!displayFontFamily || !areFontsLoaded) {
      return undefined;
    }

    return getFontFamilyStyleString(displayFontFamily);
  };

  const openBrandKitsPage = (): void => {
    trackGA4CustomEvent(GA4EventName.AICHAT_BRANDKIT_CREATE);
    armBrandKitsRefetchOnTabReturn();
    void onAddBrandAssetClickedHandler(true);
  };

  const getCreateBrandKitRow = (): ReactElement => {
    return (
      <ClickableDiv role="menuitem" className={styles.row} onClick={openBrandKitsPage}>
        <Text val={window.i18next.t('pmwjs_create_new_brand_kit')} size={TextSize.MD} className={styles.rowLabel} />
        <Icon icon="icon-plus" size={IconSize.SIZE_ICON_16} />
      </ClickableDiv>
    );
  };

  const getBrandKitRows = (): ReactElement[] => {
    return brandKitRows.map(({brandKitWithAssets, displayFontFamily}) => {
      const {brandKit} = brandKitWithAssets;
      const isSelected = brandKit.brandId === pinnedBrandKitId;

      return (
        <ClickableDiv
          key={brandKit.brandId}
          role="menuitemradio"
          aria-checked={isSelected}
          className={getRowClasses(isSelected)}
          style={{fontFamily: getFontFamilyStyle(displayFontFamily)}}
          onClick={(): void => {
            onBrandKitSelect(brandKit.brandId);
          }}
        >
          <Text val={brandKit.name} size={TextSize.MD} bold className={styles.rowLabel} />
          <BrandKitSwatchCluster
            logoURL={getBrandKitLogoURL(brandKitWithAssets.logos)}
            colorHexes={getOrderedBrandColorHexes(brandKitWithAssets.colors, BRAND_KIT_SWATCH_COLORS_LIMIT)}
          />
        </ClickableDiv>
      );
    });
  };

  const getNoneRow = (): ReactElement => {
    const isSelected = pinnedBrandKitId === null;

    return (
      <ClickableDiv
        key="brand-kit-none"
        role="menuitemradio"
        aria-checked={isSelected}
        className={getRowClasses(isSelected)}
        onClick={(): void => {
          onBrandKitSelect(null);
        }}
      >
        <Text val={window.i18next.t('pmwjs_none')} size={TextSize.MD} bold className={styles.rowLabel} />
      </ClickableDiv>
    );
  };

  const getSkeletonRows = (): ReactElement[] => {
    return Array.from({length: BRAND_KIT_SKELETON_ROW_COUNT}, (_, index) => {
      return <SkeletonBrick key={`brand-kit-skeleton-${index}`} className={styles.skeletonRow} />;
    });
  };

  const getEmptyState = (): ReactElement => {
    return (
      <div className={styles.emptyState}>
        <Text val={window.i18next.t('pmwjs_no_brand_kits_yet')} size={TextSize.MD} bold />
        <Text val={window.i18next.t('pmwjs_create_one_to_apply_your_brand')} size={TextSize.SM} className={styles.emptyStateSubtext} />
      </div>
    );
  };

  /** Only the sheet has a header for the shadow to read against — the desktop popup would just look bruised. */
  const handleListScroll = (event: UIEvent<HTMLDivElement>): void => {
    setIsListScrolled(isFullWidth && event.currentTarget.scrollTop > 0);
  };

  const getListContent = (): ReactElement | ReactElement[] => {
    if (brandKitRows.length === 0) {
      return getEmptyState();
    }

    return getBrandKitRows();
  };

  const getContent = (): ReactElement => {
    if (isLoading) {
      return (
        <div className={`${styles.list} ${styles.loadingList}`} role="none">
          {getSkeletonRows()}
        </div>
      );
    }

    return (
      <>
        <div className={`${styles.list} ${isListScrolled ? styles.scrolledList : ''}`} role="none" onScroll={handleListScroll}>
          {getCreateBrandKitRow()}
          {getListContent()}
        </div>
        <div className={doesListScroll ? styles.dividedNoneSection : styles.noneSection} role="none">
          {getNoneRow()}
        </div>
      </>
    );
  };

  return (
    <div className={`${styles.popupContent} ${isFullWidth ? styles.fullWidthPopupContent : ''}`} role="menu" aria-label={window.i18next.t('pmwjs_brand_kits')} aria-busy={isLoading}>
      {getContent()}
    </div>
  );
}
